import React, { useEffect, useRef, useState } from 'react'
import CatalogueDetails from './CatalogueDetails';
import VehicleDetail from './VehicleDetail';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { DateTime as d, Settings } from "luxon";
import { BOOKING_SOURCE, BookingType, IAddonRequirementInput, IBooking, IBookingAddonRequirement, IBookingCreateInput, ICustomerInfoInput, IOneWayRentalFee, ISubscriptionArgs, IVehicle } from '../../../../reducers/bookings/types';
import { GET_AVAILABLE_SUBSCRIPTION_VEHICLES } from '../../../../graphql/bookings/getAvailableVehiclesForSubscriptionsQuery';
import { Box, CircularProgress, CssBaseline, Dialog, FormControl, Grid, IconButton, InputLabel, makeStyles, MenuItem, Step, StepButton, Stepper, Theme, Tooltip } from '@material-ui/core';
import Container from "@material-ui/core/Container";
import { useDispatch, useSelector } from 'react-redux';
import LocalTaxiIcon from '@material-ui/icons/LocalTaxi';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import { IAppState } from '../../../../store';
import { GET_VEHICLE_GROUP_CRITERIA, GET_VEHICLES_GROUP_CRITERIA } from '../../../../graphql/vehiclePriceGroup/getVehicleGroupCriteria';
import { initialCustomerInfo, initialValues } from '../NewBooking';
import { IBranch, IServiceLocation } from '../../../../reducers/website/types';
import { CREATE_BOOKING } from '../../../../graphql/bookings/createBookingMutation';
import { useHistory, useLocation } from 'react-router-dom';
import { SnackBarVariant } from '../../../SnackbarWrapper/SnackbarWrapper';
import { formatGraphQLErrorMessage, rateTypeName } from '../../../utils';
import { useSnackBar } from '../../../SnackBarContext/SnackBarContext';
import { RATE_TYPES } from '../../utils';
import { getNameForAuthorizedSignatory, SubscriptionFlowStage } from '../utils';
import Authenticator from "../../../views/Auth";
import { updateBooking as updateBookingAction } from '../../../../actions/bookings/actions';
import PersonalDetails from './PersonalDetails/PersonalDetails';
import { IDriverRow } from './PersonalDetails/CustomerView/AddNewDriver';
import Verification from './Verification/Verification';
import { GET_BOOKING } from '../../../../graphql/bookings/getBookingQuery';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import styles from './index.module.css';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Field } from 'formik';
import Select from "@material-ui/core/Select";
import { useTranslation } from 'react-i18next';
import { summarizeSelections, useBookingEnquiryContext } from '../../../BookingEnquiry/BookingEnquiryContext';
import { calculatePriceBreakdown, deriveBranchTaxes } from '../../../SummaryPanel/priceCalculations';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  stepper: {
    color: theme.palette.primary.light,
    "& .MuiStepLabel-label": {
      color: theme.palette.primary.light
    }
  },
  backToSelectionScreen: {
    position: "absolute",
    left: -15,
    top: -50
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
    fontWeight: 600,

  },
}));

const steps = [
  "Vehicle Catalogues",
  "Vehicle Details",
  "Personal Details",
  "Verification"
]
const stepsKeyToValue: Record<string, string> = {
  "Vehicle Catalogues": "vehicleCatalogues",
  "Vehicle Details": "vehicleDetails",
  "Personal Details": "personalDetails",
  "Verification": "verification"
}


const stepsCompletedMapInitial = [false, false, false, false, false];

interface IProps {
  handleSelectionBack: () => void;
  position: number;
}

const NewSubscription: React.FC<IProps> = (props) => {

  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const snackbar = useSnackBar();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState<number>(0)
  const [vehicles, setVehicles] = useState<IVehicle[]>([])
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const organisation = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const authState = useSelector((state: IAppState) => state.authReducer);
  const branchData = website.branches;

  const [bookingData, setBookingData] = useState<IBookingCreateInput>(
    initialValues
  );
  const [selectedVehicle, setSelectedVehicle] = useState<IVehicle>()
  const [locations, setLocations] = useState<IServiceLocation[]>([])
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [stepsCompletedMap, setStepsCompletedMap] = useState<boolean[]>(stepsCompletedMapInitial);
  const [customerInfo, setCustomerInfo] = useState<ICustomerInfoInput>(initialCustomerInfo);
  const [bookingCreationFailed, setBookingCreationFailed] = useState<boolean>(false);
  const [bookingDrivers, setBookingDrivers] = useState<IDriverRow[]>([])
  const [oneWayRentalFee, setOneWayRentalFee] = useState<IOneWayRentalFee>();
  const [isLastStepPersonalDetails, setIsLastStepPersonalDetails] = useState<boolean>(false)
  const [isLastStepVerification, setIsLastStepVerification] = useState<boolean>(false)
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const driversIdRef = useRef<string[]>([]);
  const { updateSnapshot, clearSnapshot } = useBookingEnquiryContext();

  // Reset any enquiry snapshot left over from a previous booking (e.g. its bookingReferenceNumber)
  // before this fresh journey's own effect below repopulates it.
  useEffect(() => {
    clearSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const stageByStep: { [key: number]: string } = {
      [SubscriptionFlowStage.CATALOGUE_DETAILS]: "SUBSCRIPTION_CATALOGUE",
      [SubscriptionFlowStage.VEHICLE_DETAILS]: "SUBSCRIPTION_PLAN_SELECTION",
      [SubscriptionFlowStage.PERSONAL_DETAILS]: "PERSONAL_DETAILS",
      [SubscriptionFlowStage.VERIFICATION]: "SUBSCRIPTION_VERIFICATION"
    };
    const branch = website.branches.find((b) => b.id === bookingData.branchId);
    const taxes = deriveBranchTaxes(branch);
    const priceBreakdown = calculatePriceBreakdown(bookingData, taxes, Boolean(user?.customer?.taxExempted), oneWayRentalFee);
    const rentalPrice = priceBreakdown.totalPrice ? priceBreakdown.rentalPrice : undefined;
    const insurancePrice = priceBreakdown.totalPrice ? priceBreakdown.insurancePrice : undefined;
    const addonsPrice = priceBreakdown.totalPrice ? priceBreakdown.addonsPrice : undefined;
    const estimatedTotal = priceBreakdown.totalPrice || undefined;
    updateSnapshot({
      stage: stageByStep[activeStep] || "PERSONAL_DETAILS",
      branchId: bookingData.branchId || undefined,
      vehicleGroupId: selectedVehicle?.id,
      vehicleGroupName: selectedVehicle ? `${selectedVehicle.make} ${selectedVehicle.model}`.trim() : undefined,
      pickupDateTime: bookingData.pickupDateTime,
      dropoffDateTime: bookingData.dropoffDateTime,
      rentalPrice,
      insuranceName: bookingData.insurancePolicy ? bookingData.insuranceName : undefined,
      insurancePrice,
      addonsPrice,
      estimatedTotal,
      bookingReferenceNumber: undefined,
      addons: summarizeSelections(bookingData.addonRequirements, bookingData.protectionPlan?.name)
    });
  }, [bookingData, activeStep, selectedVehicle, oneWayRentalFee, user, website, updateSnapshot]);

  const [
    loadSubscriptionVehicles,
    { loading: subscriptionVehiclesLoading }
  ] = useLazyQuery(GET_AVAILABLE_SUBSCRIPTION_VEHICLES, {
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (bookingData && bookingData.id) {
        const rateTypeName = bookingData.activeSubscription.rateTypeName

        const updatedVehicles = data.getConsumerSubscriptionCatalogue.map((vehicle: IVehicle) => ({
          ...vehicle,
          subscriptions: vehicle.subscriptions.filter(
            subscription => subscription.subscription.rateTypeName === rateTypeName && subscription.subscription.isActive
          )
        }));
        if (updatedVehicles.length) {
          const filteredVehicleFromSameBranch = updatedVehicles.filter((v: IVehicle) => v.branchId === bookingData.branchId)

          setVehicles(filteredVehicleFromSameBranch)
        }
      } else {
        const activeSubscriptionVehicles: IVehicle[] = data.getConsumerSubscriptionCatalogue
          ?.map((vehicle: IVehicle) => {
            if (!vehicle || !vehicle.subscriptions.length) return null;

            const activeSubscriptions = vehicle.subscriptions.filter(
              (subscription) => subscription.subscription.isActive
            );

            return activeSubscriptions.length
              ? { ...vehicle, subscriptions: activeSubscriptions }
              : null;
          })
          .filter(Boolean) as IVehicle[];

        setVehicles(activeSubscriptionVehicles)
      }
    }
  });

  const [loadVehicleCriteria, { loading: vehicleCriteriaLoading, data: criteriaData }] = useLazyQuery(
    GET_VEHICLES_GROUP_CRITERIA,
    {
      fetchPolicy: "network-only"
    }
  );

  const [createBookingMutation, { data: bookingCreateData, loading: bookingCreating }] = useMutation(
    CREATE_BOOKING,
    {
      onCompleted: (data) => {
        dispatch(updateBookingAction(data.consumerCreateBooking));
        history.push(`/quote?id=${data.consumerCreateBooking.id}`)
        // if (newAddedDriversId.length || driversIdRef.current.length) {
        //   addDriversToBooking({
        //     variables: {
        //       bookingId: data.consumerCreateBooking.id,
        //       drivers: newAddedDriversId.length ? newAddedDriversId : driversIdRef.current
        //     }
        //   })
        // }
        snackbar({
          message: t("snackBar:quoteCreated"),
          variant: SnackBarVariant.SUCCESS
        });
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        });
        // setBookingCreationFailed(true);
      }
    }
  );

  const [
    loadBooking,
    { loading: bookingLoading, data: bookingInfoData }
  ] = useLazyQuery(GET_BOOKING, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (!data.consumerGetBooking) {
        history.goBack()
      }
      if (data.consumerGetBooking && data.consumerGetBooking.status) {
        // setBookingStatus(data.consumerGetBooking.status)
      }
      if (data.consumerGetBooking && data.consumerGetBooking.approvedDrivers.length) {
        setBookingDrivers(data.consumerGetBooking.approvedDrivers)
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    if (website?.subscriptionSettings?.creditCheckEnabled ||
      website?.subscriptionSettings?.dvlaVerificationEnabled ||
      website?.subscriptionSettings?.idvEnabled) {
      setIsLastStepVerification(true)
    } else {
      setIsLastStepPersonalDetails(true)
    }
    setBookingData(prev => ({
      ...prev,
      pcoNumber: website?.pcoRentalOnly
    }));
  }, [website])

  useEffect(() => {
    if (location && location.search) {
      const params = new URLSearchParams(location.search);
      const bookingId = params.get("booking");
      if (bookingId) {
        setIsUpdate(true);
        return loadBooking({
          variables: {
            bookingId: bookingId
          }
        });
      } else {
        loadSubscriptionVehicles();
        loadVehicleCriteria();
      }
    } else {
      loadSubscriptionVehicles()
      loadVehicleCriteria()
    }
  }, [location, website.organisation.allowSubscriptionBookings]);

  useEffect(() => {
    if (bookingInfoData && bookingInfoData.consumerGetBooking) {
      const _booking = bookingInfoData.consumerGetBooking;
      loadVehicleCriteria({
        variables: { branchId: _booking.branchId }
      })
      formatAndSetBookingData(_booking);
      loadSubscriptionVehicles({
        variables: {
          bookingId: _booking.id
        }
      })
    }
  }, [bookingInfoData]);

  useEffect(() => {
    if (user && user.businesscustomers?.id) {
      setIsLastStepPersonalDetails(true)
      setIsLastStepVerification(false)
    }
  }, [user])
  const handleStep = (index: number) => {
    if (index < activeStep) {
      setActiveStep(index);
      const tempStepsCompMap = [...stepsCompletedMap];
      for (var i = index + 1; i < stepsCompletedMap.length; i++) {
        tempStepsCompMap[i] = false
      }
      setStepsCompletedMap(tempStepsCompMap);
    }
  }

  const formatAndSetBookingData = (booking: IBooking) => {
    const addonRequirements: IAddonRequirementInput[] = booking.addonRequirements.map(
      (addonR: IBookingAddonRequirement) => {
        return {
          addon: addonR.addon.id,
          quantity: addonR.quantity,
          name: addonR.name,
          hasFixedRate: addonR.hasFixedRate,
          rate: addonR.rate,
          displayName: addonR.displayName,
          tax: {
            title: addonR.tax && addonR.tax.title ? addonR.tax.title : "",
            value: addonR.tax && addonR.tax.value ? addonR.tax.value : 0
          },
          isSubscriptionIncluded: addonR.isSubscriptionIncluded
        };
      }
    );
    const bookingObj: IBookingCreateInput = {
      id: booking.id,
      pickupServiceLocation: booking.pickupServiceLocation?.id,
      dropoffServiceLocation: booking.dropoffServiceLocation?.id,
      pickupOtherLocation: booking.pickupOtherLocation,
      dropoffOtherLocation: booking.dropoffOtherLocation,
      pickupDateTime: booking.pickupDateTime,
      dropoffDateTime: booking.dropoffDateTime,
      longTermBooking: booking.longTermBooking,
      flightNumber: booking.flightNumber,
      vehicleGroups: [],
      insuranceRate: booking.insuranceRate,
      insurancePercentageRate: booking.insurancePercentageRate || 0,
      excess: booking.excess,
      insurancePolicy: booking.insurancePolicy?.id,
      addonRequirements,
      customer: booking.customer?.id,
      customerType: booking.customerType,
      approvedDrivers: booking.approvedDrivers,
      rateTypeName: booking.rateTypeName,
      rateTypeDuration: booking.rateTypeDuration,
      billingCycleName: booking.billingCycleName,
      billingCycleDuration: booking.billingCycleDuration,
      businessCustomer: booking.businessCustomer?.id,
      authorizedSignatory: booking.authorizedSignatory,
      pcoNumber: booking.pcoNumber,
      preBookingQuestions: booking.preBookingQuestions,
      deliveryQuestions: booking.deliveryQuestions,
      source: booking.source ? booking.source : BOOKING_SOURCE.B2B2C,
      locationSurcharges: booking.locationSurcharges,
      smartCarVehicle: booking.smartCarVehicle,
      isCarShare: booking.isCarShare || false,
      status: booking.status,
      poNumber: booking.poNumber,
      isRecurringBilling: booking.isRecurringBilling,
      billingFrequency: booking.billingFrequency,
      branchId: booking.branchId,
      isSubscription: booking.isSubscription || false,
      activeSubscriptionExpanded: booking.activeSubscription,
      bookingCategory: booking.bookingCategory,
      valetSlot: booking.valetSlot
    };
    if (booking.activeSubscription) {
      bookingObj.activeSubscription = {
        startDate: booking.activeSubscription.startDate,
        endDate: booking.activeSubscription.endDate,
        subscription: booking.activeSubscription.subscription.id,
        vehicle: booking.activeSubscription.vehicle.id || "",
        duration: booking.activeSubscription.duration,
        subscriptionAmount: booking.activeSubscription.subscriptionAmount,
        enrollmentAmount: booking.activeSubscription.enrollmentAmount,
        unlimitedMileage: booking.activeSubscription.unlimitedMileage ? true : false,
        mileageLimit: booking.activeSubscription.mileageLimit || 0,
        pricePerExtraMile: booking.activeSubscription.pricePerExtraMile || 0,
        rateTypeName: booking.activeSubscription.subscription.rateTypeName
      }
      bookingObj.deliveryAddress = booking?.deliveryAddress
    }
    setCustomerInfo({ ...customerInfo, preBookingQuestions: booking.preBookingQuestions })
    setBookingData(bookingObj);
  };

  const nextStep = (data: IBookingCreateInput, vehicle?: IVehicle, updateOnly?: boolean) => {
    if (vehicle) {
      data.branchId = vehicle?.branchId
      setSelectedVehicle(vehicle)
    }
    driversIdRef.current = data.approvedDrivers
    delete data.defaultBillingCycle;
    const tempStepsCompMap = [...stepsCompletedMap];
    if (data) {
      setBookingData(data);
    }
    if (!updateOnly) {
      if (activeStep <= Object.keys(stepMapper).length - 1) {
        if (activeStep === SubscriptionFlowStage.VEHICLE_DETAILS && !authState.user?.id) {
          setVerificationDialog(true)
          setActiveStep(activeStep => activeStep + 1)
        }
        else if (
          (activeStep === SubscriptionFlowStage.PERSONAL_DETAILS && isLastStepPersonalDetails) ||
          (activeStep === SubscriptionFlowStage.VERIFICATION && isLastStepVerification)
        ) {
          const booking = {
            ...bookingData,
            ...data,
          };
          booking.vehicleGroups = booking.vehicleGroups.filter((item) => item.vehicleIds.length > 0);
          createBooking({ ...booking, ...(booking.id && { bookingId: booking.id }) });
        } else {
          tempStepsCompMap[activeStep] = true;
          setStepsCompletedMap(tempStepsCompMap);
          setActiveStep(activeStep + 1);
        }
      }
    }
    // setActiveStep(prev => prev + 1)
  }

  const prevStep = () => {
    setActiveStep(prev => prev - 1)
  }

  const onSelectAvailabilityDate = (date: string) => {
    setBookingData({
      ...bookingData,
      pickupDateTime: date
    })
  }

  const createBooking = (bookingInput: IBookingCreateInput) => {
    const addonRequirements = bookingInput.addonRequirements.map(
      (addonReq: IAddonRequirementInput) => {
        return {
          addon: addonReq.addon,
          hasFixedRate: addonReq.hasFixedRate,
          name: addonReq.name,
          displayName: addonReq.displayName,
          quantity: addonReq.quantity,
          rate: addonReq.rate,
          tax: {
            title: addonReq.tax && addonReq.tax.title ? addonReq.tax.title : "",
            value: addonReq.tax && addonReq.tax.value ? addonReq.tax.value : 0
          },
          isSubscriptionIncluded: addonReq.isSubscriptionIncluded
        };
      }
    );
    const booking: any = {
      ...(bookingInput.bookingId && { bookingId: bookingInput.bookingId }),
      pickupServiceLocation: bookingInput.pickupServiceLocation,
      pickupDateTime: d.fromISO(bookingInput.pickupDateTime).toUTC().toISO(),
      dropoffServiceLocation: bookingInput.dropoffServiceLocation,
      dropoffDateTime: bookingInput.dropoffDateTime
        ? d.fromISO(bookingInput.dropoffDateTime).toUTC().toISO()
        : bookingInput.dropoffDateTime,
      vehicleGroups: bookingInput.vehicleGroups,
      insuranceRate: bookingInput.insuranceRate,
      insurancePercentageRate: bookingInput.insurancePercentageRate || 0,
      excess: bookingInput.excess,
      insurancePolicy: bookingInput.insurancePolicy,
      addonRequirements,
      customer: bookingInput.customer ? bookingInput.customer : authState.user?.customerId,
      businessCustomer: bookingInput.businessCustomer ? bookingInput.businessCustomer : authState.user?.businesscustomers?.id,
      rateTypeName: bookingInput.rateTypeName,
      rateTypeDuration: bookingInput.rateTypeDuration,
      preBookingQuestions: bookingInput.preBookingQuestions,
      customerNote: bookingInput.customerNote,
      smartCarVehicle: bookingInput.smartCarVehicle,
      isCarShare: bookingInput.isCarShare,
      locationSurcharges: bookingInput.locationSurcharges,
      createB2B2CUser: true,
      isRecurringBilling: bookingInput.isRecurringBilling,
      billingFrequency: bookingInput.billingFrequency,
      isSubscription: bookingInput.isSubscription || false,
      isCoi: bookingInput.isCoi || false,
      pickupOtherLocation: bookingInput.pickupOtherLocation,
      dropoffOtherLocation: bookingInput.dropoffOtherLocation,
      bookingCategory: BookingType.SUBSCRIPTION,
      deliveryAddress: bookingInput.deliveryAddress,
      valetSlot: bookingInput.valetSlot,
      authorizedSignatory: bookingInput.authorizedSignatory,
      pcoNumber: bookingInput.pcoNumber || false
    };
    if (bookingInput.costCenter) {
      booking.costCenter = bookingInput.costCenter;
      if (bookingInput.projectId) {
        booking.projectId = bookingInput.projectId;
      }
    }
    if (bookingInput.isSubscription) {
      booking.activeSubscription = bookingInput.activeSubscription
      booking.locationSurcharges = []
    }
    // setBookingCreationFailed(false);
    createBookingMutation({
      variables: {
        branchId: bookingData.branchId,
        booking
      }
    });
  };

  const onSelectSubscription = (selectedSubscription: ISubscriptionArgs, vehicle: IVehicle, startDate: string) => {
    const booking = { ...bookingData }
    if (selectedSubscription && selectedSubscription?.subscription && selectedSubscription.subscription.id) {
      const weeklySubscription = selectedSubscription.subscription.rateTypeName === RATE_TYPES.WEEKLY ? true : false
      const durationKey = selectedSubscription.subscription.rateTypeName === "monthly" ? "month" : "week";

      const currentBranch = website.branches.find(branch => branch.id === bookingData.branchId)
      const vehicleCurrentLocation = vehicle.currentServiceLocation?.isDisabled ? null : vehicle.currentServiceLocation
      const currentLocation = vehicleCurrentLocation ? vehicleCurrentLocation : currentBranch?.activeLocations[0]

      const endDate = d.fromISO(startDate || "").setZone(currentBranch?.timeZone || "Europe/London").plus({ [durationKey]: selectedSubscription?.pricing?.duration }).toUTC().toISO()

      const activeSubscription = {
        duration: selectedSubscription?.pricing?.duration,
        endDate,
        enrollmentAmount: selectedSubscription?.pricing?.enrollmentAmount,
        subscriptionAmount: selectedSubscription?.pricing?.subscriptionAmount,
        startDate: startDate,
        subscription: selectedSubscription?.subscription?.id,
        vehicle: vehicle?.id || "",
        unlimitedMileage: selectedSubscription?.pricing?.unlimitedMileage,
        mileageLimit: selectedSubscription?.pricing?.mileage,
        pricePerExtraMile: selectedSubscription?.pricing?.pricePerExtraMile,
        swapsAllowed: selectedSubscription?.pricing?.swapsAllowed,
        deposit: selectedSubscription.deposit,
        rateTypeName: selectedSubscription?.subscription?.rateTypeName,
      }

      
      let includedAddons: IAddonRequirementInput[] = []
      if (booking?.activeSubscription?.subscription !== selectedSubscription?.subscription.id) {
        if (selectedSubscription?.subscription?.addons?.length) {
          selectedSubscription.subscription.addons.forEach(addon => {
            includedAddons.push({
              addon: addon.id,
              hasFixedRate: addon.hasFixedRate,
              name: addon.name,
              quantity: 1,
              rate: 0,
              displayName: addon.displayName,
              tax: {
                title: "",
                value: 0
              },
              taxable: false,
              isSubscriptionIncluded: true
            })
          })
        } else {
          includedAddons = []
        }
      } else {
        includedAddons = booking.addonRequirements
      }

      let insurancePolicy = ""
      let insuranceName = ""
      let insuranceRate = 0

      if (booking?.activeSubscription?.subscription !== selectedSubscription?.subscription.id) {
        if (selectedSubscription?.subscription?.insurancePolicy) {
          insurancePolicy = selectedSubscription.subscription.insurancePolicy.id
          insuranceName = selectedSubscription.subscription.insurancePolicy.name
          insuranceRate = 0
        } else {
          insurancePolicy = ""
          insuranceName = ""
          insuranceRate = 0
        }
      } else {
        insurancePolicy = booking.insurancePolicy || ""
        insuranceName = booking.insuranceName || ""
        insuranceRate = booking.insuranceRate || 0
      }

      setBookingData({
        ...booking,
        vehicleGroups: [],
        isSubscription: true,
        activeSubscription,
        pickupServiceLocation: currentLocation?.id || "",
        dropoffServiceLocation: currentLocation?.id || "",
        pickupDateTime: startDate,
        dropoffDateTime: endDate,
        billingCycleDuration: weeklySubscription ? 10080 : 43200,
        billingCycleName: weeklySubscription ? RATE_TYPES.WEEKLY : RATE_TYPES.MONTHLY,
        rateTypeDuration: weeklySubscription ? 10080 : 43200,
        rateTypeName: weeklySubscription ? RATE_TYPES.WEEKLY : RATE_TYPES.MONTHLY,
        billingFrequency: 1,
        isRecurringBilling: true,
        locationSurcharges: [],
        defaultBillingCycle: false,
        addonRequirements: includedAddons,
        insurancePolicy,
        insuranceName,
        insuranceRate
      })
    }
  }

  const stepMapper: any = {
    0: (
      <CatalogueDetails
        vehicles={vehicles}
        bookingData={bookingData}
        criteriaData={criteriaData}
        locations={locations}
        onSubmit={nextStep}
      />
    ),
    1: (
      <VehicleDetail
        bookingData={bookingData}
        setBookingData={setBookingData}
        selectedVehicle={selectedVehicle}
        onSubmit={nextStep}
        onSelectAvailabilityDate={onSelectAvailabilityDate}
        onSelectSubscription={onSelectSubscription}
      />
    ),
    2: (
      <PersonalDetails
        bookingData={bookingData}
        setBookingData={setBookingData}
        onSubmit={nextStep}
        onPrevious={prevStep}
        customerInfo={customerInfo}
        bookingCreationFailed={bookingCreationFailed}
        bookingDrivers={bookingDrivers}
        oneWayRentalFee={oneWayRentalFee}
        updateBookingDrivers={(drivers) => {
          if (drivers.length) {
            const driversId = drivers
              .filter((driver) => typeof driver.id === 'string')
              .map((driver) => driver.id as string);

            if (driversId.length) {
              if (bookingData?.approvedDrivers?.length) {
                const newDriversId = driversId.filter((id) => !bookingData.approvedDrivers.some((approvedDriver: IDriverRow) => approvedDriver.id === id));
                // setNewAddedDriversId(newDriversId);
                // driversIdRef.current = newDriversId;
              } else {
                // setNewAddedDriversId(driversId);
                // driversIdRef.current = driversId;
              }
            }
            setBookingDrivers(drivers);
          }
        }}
        isLastStepPersonalDetails={isLastStepPersonalDetails}
      />
    ),
    3: (
      <Verification
        onSubmit={nextStep}
        customerInfo={customerInfo}
        bookingData={bookingData}
      />
    )
  };

  const getStepIcon = (step: string, activeStep: number) => {
    switch (step) {
      case "Vehicle Catalogues":
        return <LocalTaxiIcon style={{ color: activeStep === 0 ? "rgba(255, 199, 0, 1)" : "rgba(0,0,0,0.5)", fontSize: 36 }} />
      case "Vehicle Details":
        return <LocalTaxiIcon style={{ color: activeStep === 1 ? "rgba(255, 199, 0, 1)" : "rgba(0,0,0,0.5)", fontSize: 36 }} />
      case "Personal Details":
        return <AccountCircleIcon style={{ color: activeStep === 2 ? "rgba(255, 199, 0, 1)" : "rgba(0,0,0,0.5)", fontSize: 36 }} />
      case "Verification":
        return <AccountCircleIcon style={{ color: activeStep === 3 ? "rgba(255, 199, 0, 1)" : "rgba(0,0,0,0.5)", fontSize: 36 }} />
    }
  }
  return (
    <Container maxWidth={false} style={{ marginTop: props.position + 35, maxWidth: '1440px' }}>
      <Grid container spacing={2}>
        <CssBaseline />
        <Grid item xs={12}>
          <Box style={{ width: "100%", margin: "0 auto", position: "relative" }}>
            {
              (website.bookingTypes.length > 1 && (
                <div className={classes.backToSelectionScreen}>
                  <Tooltip title={t("root:backToBookingCategorySelection")}>
                    <IconButton
                      onClick={props.handleSelectionBack}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              )
              )
            }

            <Stepper nonLinear activeStep={activeStep} style={{ marginTop: 10 }}>
              {steps.map((label, index) => {
                if (
                  (label === "Verification" && !isLastStepVerification)) {
                  return;
                }
                const stepProps = {};
                return <Step completed={stepsCompletedMap[index]} key={label} {...stepProps}>
                  <StepButton
                    onClick={() => handleStep(index)}
                    completed={stepsCompletedMap[index]}
                    className={stepsCompletedMap[index] ? classes.stepper : ""}
                  >
                    {t<string>(`root:${stepsKeyToValue[label]}`)}
                  </StepButton>
                </Step>
              })}
            </Stepper>
          </Box>
        </Grid>

        {
          (subscriptionVehiclesLoading || vehicleCriteriaLoading || bookingLoading) ?
            (
              <Grid container justify="center" alignItems="center" style={{ height: "50vh" }} item xs={12}>
                <CircularProgress />
              </Grid>
            )
            : (
              <Grid container item xs={12}>
                <Grid item xs={12}><Box mt={2}></Box></Grid>
                <Box style={{ width: "100%" }}>
                  {bookingCreating ? <Grid item xs={12} container justify='center' alignItems='center' style={{ height: 400 }} > <CircularProgress /></Grid> : stepMapper[activeStep]}
                </Box>
              </Grid>
            )
        }

      </Grid >

      {
        verificationDialog && (
          <Dialog
            open={verificationDialog}
            onClose={() => {
              if (authState && authState.authUser?._id) {
                setActiveStep(prev => prev + 1)
              }
            }}
          >
            <div style={{ padding: 5 }}>
              <Authenticator onSignIn={() => setVerificationDialog(false)} />
            </div>
          </Dialog>
        )
      }
    </Container>
  )
}

export default NewSubscription
